import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { contractsAPI, usageAPI, formatApiError } from '@/lib/api';
import { Eye, Upload, FileText, Clock, ChevronRight, Loader2, LogOut, Settings, AlertTriangle, Clipboard, Lock, Briefcase, Cloud, Users, Truck, BookOpen, GitCompare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200" data-testid="dashboard-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
          <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
        </Link>
        <div className="flex items-center gap-4">
          <span className="font-body text-sm text-zinc-500 hidden sm:block">{user?.email}</span>
          <Link to="/settings" className="p-2 hover:bg-zinc-100 transition-colors" data-testid="nav-settings-btn"><Settings className="w-4 h-4 text-zinc-600" /></Link>
          <button onClick={async () => { await logout(); navigate('/'); }} className="p-2 hover:bg-zinc-100 transition-colors" data-testid="nav-logout-btn"><LogOut className="w-4 h-4 text-zinc-600" /></button>
        </div>
      </div>
    </header>
  );
}

function UsageBar({ usage }) {
  if (!usage) return null;
  const pct = Math.min(100, (usage.scans_used / usage.scan_limit) * 100);
  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="usage-bar">
      <div className="flex items-center justify-between mb-3">
        <span className="font-body text-sm font-medium text-zinc-700">Monthly Scans</span>
        <span className="font-mono text-sm text-zinc-900">{usage.scans_used} / {usage.scan_limit}</span>
      </div>
      <div className="w-full h-2 bg-zinc-100 overflow-hidden">
        <div className={`h-full transition-all duration-300 ${pct >= 100 ? 'bg-rose-500' : pct >= 75 ? 'bg-amber-500' : 'bg-brand'}`} style={{ width: `${pct}%` }} />
      </div>
      {pct >= 100 && (
        <p className="mt-2 font-body text-xs text-rose-600">Scan limit reached. <Link to="/settings" className="underline font-medium">Upgrade to Pro</Link></p>
      )}
    </div>
  );
}

function UploadArea({ onUploadComplete, usage, isAnalyzing, setIsAnalyzing }) {
  const [dragActive, setDragActive] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteTitle, setPasteTitle] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'docx'].includes(ext)) {
      toast.error('Unsupported file type. Please upload PDF or DOCX.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await contractsAPI.upload(formData);
      toast.success('Analysis complete!');
      navigate(`/analysis/${res.data.report_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handlePaste = async () => {
    if (!pasteText.trim() || pasteText.trim().length < 50) {
      toast.error('Please enter at least 50 characters of contract text.');
      return;
    }
    setIsAnalyzing(true);
    try {
      const res = await contractsAPI.analyzeText({ text: pasteText, title: pasteTitle || 'Pasted Contract' });
      toast.success('Analysis complete!');
      navigate(`/analysis/${res.data.report_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const limitReached = usage && !usage.can_scan;

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="upload-area">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-lg text-zinc-900">New Analysis</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowPaste(false)} className={`px-3 py-1.5 text-xs font-body font-medium transition-colors ${!showPaste ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}`} data-testid="upload-tab-file">Upload File</button>
          <button onClick={() => setShowPaste(true)} className={`px-3 py-1.5 text-xs font-body font-medium transition-colors ${showPaste ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}`} data-testid="upload-tab-paste">Paste Text</button>
        </div>
      </div>

      {isAnalyzing ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="analyzing-state">
          <Loader2 className="w-8 h-8 text-brand animate-spin" />
          <p className="font-body text-sm text-zinc-600">Analyzing your contract... This may take up to a minute.</p>
        </div>
      ) : limitReached ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="limit-reached">
          <AlertTriangle className="w-8 h-8 text-amber-500" />
          <p className="font-body text-sm text-zinc-600">You've reached your monthly scan limit.</p>
          <Link to="/settings" className="bg-brand text-white px-6 py-2 font-body text-sm font-medium" data-testid="upgrade-btn">Upgrade to Pro</Link>
        </div>
      ) : !showPaste ? (
        <div
          className={`border-2 border-dashed ${dragActive ? 'border-brand bg-blue-50/50' : 'border-zinc-200'} p-12 flex flex-col items-center justify-center cursor-pointer transition-colors`}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => fileInputRef.current?.click()}
          data-testid="upload-dropzone"
        >
          <Upload className="w-8 h-8 text-zinc-400 mb-3" strokeWidth={1.5} />
          <p className="font-body text-sm text-zinc-600 mb-1">Drag and drop your contract here</p>
          <p className="font-body text-xs text-zinc-400">PDF or DOCX, up to 10MB</p>
          <input ref={fileInputRef} type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => handleFile(e.target.files[0])} data-testid="file-input" />
        </div>
      ) : (
        <div className="space-y-3">
          <input
            type="text"
            value={pasteTitle}
            onChange={(e) => setPasteTitle(e.target.value)}
            placeholder="Contract title (optional)"
            className="w-full border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-brand px-4 py-2 font-body text-sm outline-none transition-colors"
            data-testid="paste-title-input"
          />
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Paste your contract text here..."
            rows={8}
            className="w-full border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-brand px-4 py-3 font-body text-sm outline-none transition-colors resize-y"
            data-testid="paste-text-input"
          />
          <button onClick={handlePaste} className="bg-brand text-white hover:bg-brand-hover px-6 py-2.5 font-body font-medium text-sm transition-colors" data-testid="paste-analyze-btn">
            Analyze Contract
          </button>
        </div>
      )}

      <div className="mt-4 bg-amber-50 border border-amber-200 px-4 py-2" data-testid="upload-disclaimer">
        <p className="font-body text-xs text-amber-800">This tool provides risk insights only and does not constitute legal advice.</p>
      </div>
    </div>
  );
}

function RecentAnalyses({ reports, loading }) {
  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 p-6">
        <h2 className="font-heading font-bold text-lg text-zinc-900 mb-4">Recent Analyses</h2>
        <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-zinc-400 animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="recent-analyses">
      <h2 className="font-heading font-bold text-lg text-zinc-900 mb-4">Recent Analyses</h2>
      {reports.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-analyses">
          <FileText className="w-8 h-8 text-zinc-300 mb-3" />
          <p className="font-body text-sm text-zinc-500">No analyses yet</p>
          <p className="font-body text-xs text-zinc-400 mt-1">Upload a contract to get started</p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {reports.map((r) => (
            <Link key={r.id} to={`/analysis/${r.id}`} className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-2 px-2 transition-colors group" data-testid={`report-item-${r.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-zinc-400 shrink-0" />
                <div className="min-w-0">
                  <p className="font-body text-sm font-medium text-zinc-800 truncate">{r.file_name}</p>
                  <p className="font-body text-xs text-zinc-400">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <RiskBadge score={r.risk_score} />
                <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-zinc-500 transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function RiskBadge({ score }) {
  let bg, text;
  if (score >= 7) { bg = 'bg-rose-100 text-rose-700 border-rose-200'; }
  else if (score >= 4) { bg = 'bg-amber-100 text-amber-700 border-amber-200'; }
  else { bg = 'bg-emerald-100 text-emerald-700 border-emerald-200'; }
  return (
    <span className={`${bg} border px-2 py-0.5 font-mono text-xs font-bold`} data-testid="risk-badge">{score}/10</span>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [usage, setUsage] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [reportsRes, usageRes, templatesRes] = await Promise.all([
        contractsAPI.list(),
        usageAPI.get(),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/templates`),
      ]);
      setReports(reportsRes.data.reports || []);
      setUsage(usageRes.data);
      setTemplates(templatesRes.data.templates || []);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoadingReports(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="dashboard-page">
      <Navbar />
      <main className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading font-bold text-2xl tracking-tight text-zinc-900">Dashboard</h1>
              <p className="font-body text-sm text-zinc-500 mt-1">
                {user?.plan_type === 'pro_plus' ? 'Pro Plus Plan' : user?.subscription_status === 'active' ? 'Pro Plan' : 'Free Plan'}
                {user?.subscription_status !== 'active' && (
                  <> &mdash; <Link to="/settings" className="text-brand underline" data-testid="upgrade-link">Upgrade</Link></>
                )}
              </p>
            </div>
            <Link to="/compare" className="flex items-center gap-2 border border-zinc-200 bg-white hover:bg-zinc-50 px-4 py-2 font-body text-sm font-medium text-zinc-700 transition-colors" data-testid="compare-contracts-btn">
              <GitCompare className="w-4 h-4 text-brand" strokeWidth={1.5} />
              Compare
            </Link>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <UploadArea onUploadComplete={loadData} usage={usage} isAnalyzing={isAnalyzing} setIsAnalyzing={setIsAnalyzing} />

              {/* Template Cards */}
              {templates.length > 0 && (
                <div className="bg-white border border-zinc-200 p-6" data-testid="template-cards">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-brand" strokeWidth={1.5} />
                      <h2 className="font-heading font-bold text-lg text-zinc-900">Template Library</h2>
                    </div>
                    <Link to="/templates" className="font-body text-xs text-brand font-medium hover:underline" data-testid="view-all-templates">View All</Link>
                  </div>
                  <p className="font-body text-xs text-zinc-500 mb-4">Explore pre-built analyses to see what ContractEye detects for each contract type.</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {templates.slice(0, 3).map((t) => {
                      const iconMap = { lock: Lock, briefcase: Briefcase, cloud: Cloud, users: Users, truck: Truck };
                      const Icon = iconMap[t.icon] || Lock;
                      let riskBg = 'bg-amber-100 text-amber-700 border-amber-200';
                      if (t.risk_preview >= 7) riskBg = 'bg-rose-100 text-rose-700 border-rose-200';
                      else if (t.risk_preview < 4) riskBg = 'bg-emerald-100 text-emerald-700 border-emerald-200';
                      return (
                        <Link
                          key={t.slug}
                          to={`/templates/${t.slug}`}
                          className="border border-zinc-200 p-4 hover:bg-zinc-50 transition-colors group"
                          data-testid={`dashboard-template-${t.slug}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Icon className="w-4 h-4 text-brand" strokeWidth={1.5} />
                            <span className={`${riskBg} border font-mono text-xs font-bold px-1.5 py-0.5`}>{t.risk_preview}/10</span>
                          </div>
                          <h4 className="font-heading font-bold text-sm text-zinc-900 mb-1 group-hover:text-brand transition-colors">{t.title}</h4>
                          <p className="font-body text-xs text-zinc-500 leading-relaxed line-clamp-2">{t.description}</p>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              <RecentAnalyses reports={reports} loading={loadingReports} />
            </div>
            <div className="space-y-6">
              <UsageBar usage={usage} />
              <div className="bg-white border border-zinc-200 p-6" data-testid="quick-stats">
                <h3 className="font-heading font-bold text-sm text-zinc-700 mb-4">Quick Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-body text-sm text-zinc-500">Total Analyses</span>
                    <span className="font-mono text-sm text-zinc-900">{reports.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body text-sm text-zinc-500">Plan</span>
                    <span className="font-mono text-sm text-zinc-900">{user?.plan_type === 'pro_plus' ? 'Pro Plus' : user?.subscription_status === 'active' ? 'Pro' : 'Free'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
